import React, {useEffect, useState, useRef} from 'react';
import styled from "styled-components";
import {ButtonToggle, Input} from "reactstrap";
import moment from "moment";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faChevronUp} from "@fortawesome/free-solid-svg-icons";
import {regexNumber} from "../../../Utils/Utils";

const TimeContainer = styled.div`
  display: inline-flex;
  
  @media(max-width: 992px) {
    margin-left: 10px;
  }
  
  & > div {
    max-width: 60px;
    text-align: center;
    border: 1px solid #ced4da;
    border-radius: 5px;
  }
  
  & > div:focus-within {
    box-shadow: 0 0 0 .1rem #ACACAC;
  }
  
  & > div button {
    border: 0;
    width: 100%;
  }
  
  & > div button:focus {
     outline: none;
  }
  
  & > div:hover {
     border-color: #ACACAC;
     box-shadow: 0 0 0 .05rem #ACACAC;
  }
  
  & > div button:hover {
     background: #ACACAC;
  }
  
  & > div button:hover:first-child {
    border-top-right-radius: 3px;
    border-top-left-radius: 3px;
  }
  
  & > div button:hover:last-child {
    border-bottom-right-radius: 3px;
    border-bottom-left-radius: 3px;
  }
  
  & > div button:first-child {
    border-top-right-radius: 5px;
    border-top-left-radius: 5px;
  }
  
  & > div button:last-child {
    border-bottom-right-radius: 5px;
    border-bottom-left-radius: 5px;
  }
  
  & input {
    text-align: center;
    border: 0;
  }
  
  & input:focus {
    box-shadow: none;
  }
  
  & > input::-webkit-outer-spin-button,
      input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
  
  & > input[type=number] {
    -moz-appearance: textfield;
  }
  
  & > button {
    margin-left: 2px;
    width: 60px;
  }
  
  & > b {
    display: flex;
    align-self: center;
    margin: 0 2px;
  }
`

export enum TimeConvention {
  AM="AM",
  PM="PM"
}

interface ITimeProps {
  value?: Date;
  onChangeTime: (time: Date) => void;
}

const TimePickerComponent = ({value = new Date(), onChangeTime }: ITimeProps) => {

  const [hours, setHours] = useState<string>("00");
  const [minutes, setMinutes] = useState<string>("00");
  const [timeConvention, setTimeConvention] = useState<TimeConvention>(TimeConvention.AM);
  const [skipValidationHours, setSkipValidationHours] = useState<boolean>(false);
  const [skipValidationMinutes, setSkipValidationMinutes] = useState<boolean>(false);
  const isSetupValue = useRef<boolean>(false);
  const lastTime = useRef<Date>(value);

  const _onChangeTimeConvention = (e?: any) => {
    e?.preventDefault();
    setTimeConvention( timeConvention === TimeConvention.AM ? TimeConvention.PM : TimeConvention.AM );
  }

  const _onUpdateHours = (value: any) => {
    if (skipValidationHours || (skipValidationHours && value === "")) return setHours(value);

    if (parseInt(value) === 12) _onChangeTimeConvention();
    if (parseInt(hours) - parseInt(value) === 1 && parseInt(value) === 11) _onChangeTimeConvention();
    if (parseInt(value) - parseInt(hours) === 11 && parseInt(value) === 12) _onChangeTimeConvention();

    if (value > 12) value = 1;

    if (value < 1) value = 12;

    if (skipValidationHours) setHours(value);
    else setHours(('0' + value).slice(-2));
  }

  const _onChangeHours = (e: any) => _onUpdateHours(e.currentTarget.value);

  const _onUpdateMinutes = (value: any) => {
    if (skipValidationMinutes && value === "") return setMinutes(value);

    let nextHour = parseInt(hours);

    if (parseInt(value) < 0) {
      value = "50";
      nextHour--;
      _onUpdateHours(nextHour.toString());
    }

    if (parseInt(value) > 59) {
      value = "00";
      nextHour++;
      _onUpdateHours(nextHour.toString());
    }

    if (skipValidationMinutes) setMinutes(value)
    else setMinutes(('0' + value).slice(-2));
  }

  const _onChangeMinutes = (e: any) => {
    let value = parseInt(e.currentTarget.value).toString();
    _onUpdateMinutes(value);
  }

  const _skipValidation = (e: any): boolean => {
    return (
      !regexNumber.test(e.key) ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "Backspace"
    );
  }

  const _onKeyDownHours = (e: any) => setSkipValidationHours(_skipValidation(e));

  const _onKeyDownMinutes = (e: any) => setSkipValidationMinutes(_skipValidation(e));

  const _onBlurHours = (e: any) => {
    const value = e.currentTarget.value;
    if (value === "") return setHours("01");
    setHours(('0' + value).slice(-2));
  }

  const _onBlurMinutes = (e: any) => {
    const value = e.currentTarget.value;
    if (value === "") return setMinutes("01");
    setMinutes(('0' + value).slice(-2));
  }

  const _onClickHours = (e: any, increment: boolean) => {
    e.preventDefault();
    const newHour = increment ? parseInt(hours) + 1 : parseInt(hours) - 1;
    _onUpdateHours(newHour);
  }

  const _onClickMinutes = (e: any, increment: boolean) => {
    e.preventDefault();
    const newMinute = increment ? parseInt(minutes) + 1 : parseInt(minutes) - 1;
    _onUpdateMinutes(newMinute);
  }

  useEffect(() => {
    if ( value && !isSetupValue.current) {

      setHours(moment(value).format("hh"));
      setMinutes(moment(value).format("mm"))
      setTimeConvention(
    moment(value).format("A") === TimeConvention.AM.toString()
          ? TimeConvention.AM
          : TimeConvention.PM
      );

      isSetupValue.current = true;
    }
  }, [value, setMinutes, setHours])

  useEffect(() => {
    if (
      hours === "" ||
      minutes === "" ||
      isNaN(minutes as any) ||
      isNaN(hours as any) ||
      skipValidationMinutes ||
      skipValidationHours
    ) return;

    let time = new Date();
    time.setHours(
  timeConvention === TimeConvention.AM
        ? (parseInt(hours) === 12 ? parseInt(hours) - 12 : parseInt(hours))
        : (parseInt(hours) === 12 ? parseInt(hours) : parseInt(hours) + 12)
    );
    time.setMinutes(parseInt(minutes));

    if (
      lastTime.current.getHours() !== time.getHours() ||
      lastTime.current.getMinutes()!== time.getMinutes()
    ) {
      lastTime.current = time;
      onChangeTime(time);
    }
  }, [hours, minutes, timeConvention, onChangeTime, skipValidationHours, skipValidationMinutes]);

  return (
    <TimeContainer>
      <div>
        <button tabIndex={-1} onClick={(e) => _onClickHours(e, true)}>
          <FontAwesomeIcon icon={faChevronUp}/>
        </button>
        <Input
          value={hours}
          type="number"
          step={1}
          onChange={_onChangeHours}
          onKeyDown={_onKeyDownHours}
          onBlur={_onBlurHours}
        />
        <button tabIndex={-1} onClick={(e) => _onClickHours(e, false)}>
          <FontAwesomeIcon icon={faChevronDown}/>
        </button>
      </div>
      <b>:</b>
      <div>
        <button tabIndex={-1} onClick={(e) => _onClickMinutes(e, true)}>
          <FontAwesomeIcon icon={faChevronUp}/>
        </button>
        <Input
          value={minutes}
          type="number"
          step={10}
          onChange={_onChangeMinutes}
          onKeyDown={_onKeyDownMinutes}
          onBlur={_onBlurMinutes}
        />
        <button tabIndex={-1} onClick={(e) => _onClickMinutes(e, false)}>
          <FontAwesomeIcon icon={faChevronDown}/>
        </button>
      </div>
      &nbsp;
      <ButtonToggle
        onClick={_onChangeTimeConvention}
      >
        {timeConvention}
      </ButtonToggle>
    </TimeContainer>
  );
};
export default TimePickerComponent;
